import React, { useEffect } from 'react';
import { Shield } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { companyInfo } from '../data/mock';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentDate = new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-800">Privacy Policy</h1>
              <p className="text-slate-500">G&B Wood</p>
            </div>
          </div>
          <p className="text-lg text-slate-700 font-medium">
            Informativa Privacy ai sensi del Regolamento (UE) 2016/679 ("GDPR")
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            
            {/* Titolare del trattamento */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">Titolare del trattamento</h2>
              <div className="bg-slate-50 rounded-xl p-6 mb-4">
                <p className="text-slate-700 font-semibold">G&B Wood</p>
                <p className="text-slate-600">Sede operativa: {companyInfo.address}</p>
                <p className="text-slate-600">Email: {companyInfo.email}</p>
                <p className="text-slate-600">Telefono: {companyInfo.phone}</p>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Il Titolare tratta i dati personali degli utenti nel rispetto del GDPR e della normativa italiana vigente (D.lgs. 196/2003 e s.m.i.).
              </p>
            </section>

            {/* 1. Tipologie di dati trattati */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">1. Tipologie di dati trattati</h2>
              
              <h3 className="text-lg font-semibold text-slate-700 mb-3">Dati forniti volontariamente dall'utente</h3>
              <p className="text-slate-600 leading-relaxed mb-3">
                Compilando i form presenti sul sito, l'utente può fornire:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-6 space-y-1">
                <li>nome e cognome</li>
                <li>indirizzo email</li>
                <li>numero di telefono</li>
                <li>città/zona dell'intervento</li>
                <li>eventuale descrizione del lavoro richiesto</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-700 mb-3">Dati di navigazione</h3>
              <p className="text-slate-600 leading-relaxed mb-3">
                Durante la navigazione vengono raccolti, tramite cookie tecnici:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 space-y-1">
                <li>indirizzo IP</li>
                <li>tipo di dispositivo e browser</li>
                <li>pagine visitate</li>
                <li>orario della richiesta</li>
              </ul>
              <p className="text-slate-600 leading-relaxed">
                Non vengono raccolti dati appartenenti a categorie particolari (dati sensibili).
              </p>
            </section>

            {/* 2. Finalità del trattamento e base giuridica */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">2. Finalità del trattamento e base giuridica</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                I dati sono trattati per le seguenti finalità:
              </p>

              <div className="space-y-6">
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">Gestione delle richieste di contatto e preventivo</h4>
                  <ul className="list-disc list-inside text-slate-600 mb-2 space-y-1">
                    <li>rispondere a domande</li>
                    <li>inviare preventivi</li>
                    <li>organizzare sopralluoghi</li>
                  </ul>
                  <p className="text-sm text-slate-500">
                    <strong>Base giuridica:</strong> esecuzione di misure precontrattuali (art. 6.1.b GDPR).
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">Adempimenti amministrativi, contabili e fiscali</h4>
                  <p className="text-slate-600 mb-2">(in caso di conferma del lavoro)</p>
                  <p className="text-sm text-slate-500">
                    <strong>Base giuridica:</strong> adempimento obblighi legali (art. 6.1.c GDPR).
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">Invio di comunicazioni di servizio</h4>
                  <p className="text-slate-600 mb-2">relative alle richieste dell'utente</p>
                  <p className="text-sm text-slate-500">
                    <strong>Base giuridica:</strong> legittimo interesse del Titolare (art. 6.1.f GDPR).
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">Analisi statistiche anonime sul traffico del sito</h4>
                  <p className="text-slate-600 mb-2">(solo se attivi cookie analytics)</p>
                  <p className="text-sm text-slate-500">
                    <strong>Base giuridica:</strong> consenso dell'utente (art. 6.1.a GDPR).
                  </p>
                </div>
              </div>

              <p className="text-slate-600 leading-relaxed mt-4">
                Non viene effettuata profilazione automatizzata a fini di marketing.
              </p>
            </section>

            {/* 3. Modalità del trattamento */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">3. Modalità del trattamento</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Il trattamento avviene con strumenti informatici e telematici, adottando misure di sicurezza idonee a proteggere i dati da accessi non autorizzati, perdita o uso illecito.
              </p>
              <p className="text-slate-600 leading-relaxed">
                I dati sono trattati da personale autorizzato dal Titolare e, se necessario, da fornitori esterni nominati responsabili del trattamento (es. provider hosting, strumenti di email).
              </p>
            </section>

            {/* 4. Comunicazione e destinatari dei dati */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">4. Comunicazione e destinatari dei dati</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                I dati potranno essere comunicati a:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 space-y-1">
                <li>consulenti fiscali/contabili (per adempimenti di legge)</li>
                <li>fornitori di servizi informatici/hosting</li>
                <li>soggetti che collaborano con il Titolare per l'esecuzione del servizio richiesto</li>
              </ul>
              <p className="text-slate-600 leading-relaxed">
                I dati non vengono diffusi pubblicamente.
              </p>
            </section>

            {/* 5. Conservazione dei dati */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">5. Conservazione dei dati</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Dati relativi a richieste di informazioni/preventivo:</strong> fino a 24 mesi dall'ultimo contatto.</li>
                <li><strong>Dati relativi a rapporti contrattuali:</strong> secondo i termini previsti dalla legge (fiscale/contabile).</li>
                <li><strong>Dati raccolti tramite cookie:</strong> secondo quanto indicato nella Cookie Policy.</li>
              </ul>
            </section>

            {/* 6. Diritti dell'interessato */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">6. Diritti dell'interessato</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                L'utente può esercitare in qualsiasi momento i diritti previsti dagli artt. 15–22 GDPR, tra cui:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-6 space-y-1">
                <li>accesso ai dati personali</li>
                <li>rettifica o aggiornamento</li>
                <li>cancellazione ("diritto all'oblio")</li>
                <li>limitazione del trattamento</li>
                <li>opposizione al trattamento</li>
                <li>portabilità dei dati</li>
              </ul>
              
              <div className="bg-amber-50 rounded-xl p-5 mb-4">
                <p className="text-slate-700">
                  <strong>Per esercitare i diritti:</strong><br />
                  Email: <a href={`mailto:${companyInfo.email}`} className="text-amber-600 hover:underline">{companyInfo.email}</a>
                </p>
              </div>

              <p className="text-slate-600 leading-relaxed">
                L'utente ha inoltre diritto di proporre reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">www.garanteprivacy.it</a>).
              </p>
            </section>

            {/* 7. Trasferimento di dati verso Paesi extra UE */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">7. Trasferimento di dati verso Paesi extra UE</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                In linea di principio i dati non vengono trasferiti fuori dallo Spazio Economico Europeo.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Qualora ciò avvenisse tramite fornitori esteri (es. servizi cloud), il Titolare garantirà il rispetto degli artt. 44–49 GDPR (clausole contrattuali standard o altre garanzie adeguate).
              </p>
            </section>

            {/* 8. Modifiche alla presente informativa */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">8. Modifiche alla presente informativa</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Il Titolare si riserva il diritto di modificare la presente informativa in qualsiasi momento.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Ultimo aggiornamento:</strong> {currentDate}
              </p>
            </section>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
